import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../features/auth/useUser';
import { useAuthToken } from '../../features/auth/useAuthToken';
import { useLogout } from '../../hooks/useLogout';
import { setStoredToken } from '../../services/apiClient';
import { skipOrganizationSetup } from '../../features/organizations/api';
import { brandConfig } from '../../config/brands';
import { useAuthLogo } from '../../hooks/useAuthScheme';
import { AuthBrandLogo } from '../../features/auth/AuthBrandLogo';
import './OnboardingPage.css';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { setToken } = useAuthToken();
  const logo = useAuthLogo();
  const [isSkipping, setIsSkipping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 組織概念を隠すブランド (usodeki 等) は「すぐに使い始める」を自動実行する
  const autoSkip = brandConfig.features.autoPersonalWorkspace === true;
  const autoStartedRef = useRef(false);

  // 既存ユーザー（既に組織に所属しているユーザー）はトップページにリダイレクト
  useEffect(() => {
    if (user?.organization_id) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // GTM dataLayer イベント送信（CV計測用）
  // PII (email 等) は GA4/Google Ads へ送らないため event 名のみを積む。
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'onboarding_viewed',
    });
  }, []);

  const handleSkipSetup = useCallback(async () => {
    setIsSkipping(true);
    setError(null);
    try {
      const response = await skipOrganizationSetup();
      if (response.success && response.token) {
        // GTM dataLayer イベント送信（PII は積まない）
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'onboarding_skipped',
        });

        // 新しいトークンを保存
        setStoredToken(response.token);
        setToken(response.token);

        // トップページへリダイレクト（トークン更新後、userは自動的に更新される）
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      console.error('Failed to skip organization setup:', err);
      setError(err.response?.data?.error || '個人ワークスペースの作成に失敗しました');
    } finally {
      setIsSkipping(false);
    }
  }, [setToken, navigate]);

  // autoPersonalWorkspace ブランドはマウント時に自動で個人WS作成へ進める。
  // 組織未所属のときだけ 1 度だけ実行 (所属済みは上の effect が '/' へ流す)。
  useEffect(() => {
    if (!autoSkip || !user || user.organization_id || autoStartedRef.current) return;
    autoStartedRef.current = true;
    handleSkipSetup();
  }, [autoSkip, user, handleSkipSetup]);

  const handleProceedToSetup = useCallback(() => {
    navigate('/organizations/setup');
  }, [navigate]);

  const handleLogout = useLogout();

  // 組織概念を隠すブランドでは 2 択を出さず、自動処理中のライトなローダーのみ表示。
  if (autoSkip) {
    return (
      <div className="onboarding-auto">
        <img
          src={logo.src}
          alt={brandConfig.serviceName}
          className={`onboarding-auto-logo${logo.invert ? ' onboarding-logo--invert' : ''}`}
        />
        {error ? (
          <>
            <p className="onboarding-auto-error">{error}</p>
            <button
              type="button"
              className="onboarding-auto-retry"
              onClick={() => {
                autoStartedRef.current = false;
                setError(null);
                handleSkipSetup();
              }}
            >
              もう一度試す
            </button>
          </>
        ) : (
          <div className="onboarding-auto-loading">
            <span className="loading-spinner" />
            <span>準備しています…</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <AuthBrandLogo />
        <div className="auth-card">
          <div className="auth-header">
            {user?.email && (
              <div className="user-info-block">
                <div className="user-email-badge">{user.email}</div>
                <button
                  type="button"
                  className="logout-link"
                  onClick={handleLogout}
                >
                  別のアカウントに切り替える
                </button>
              </div>
            )}
          </div>

          <div className="auth-content">
            <p className="auth-intro">
              まずはご利用方法をお選びください。
            </p>

            <div className="auth-options">
              <button
                type="button"
                className="auth-option"
                onClick={handleSkipSetup}
                disabled={isSkipping}
              >
                <div className="option-icon">
                  <img src="/startup_product_rocket_box.svg" alt="すぐに使い始める" />
                </div>
                <h2 className="option-title">すぐに使い始める</h2>
                <p className="option-description">
                  仮の組織を作成して利用開始します。後から別の組織に参加する場合、作成したデータやポイントは引き継げないためご注意ください。
                </p>
                {isSkipping && (
                  <div className="option-loading">
                    <span className="loading-spinner"></span>
                    作成中...
                  </div>
                )}
              </button>

              <button
                type="button"
                className="auth-option"
                onClick={handleProceedToSetup}
                disabled={isSkipping}
              >
                <div className="option-icon">
                  <img src="/human_resources_hierarchy_1.svg" alt="組織を作成・参加" />
                </div>
                <h2 className="option-title">組織を作成・参加する</h2>
                <p className="option-description">
                  チームで共同作業する場合や、既存の組織に参加する場合はこちら。
                </p>
              </button>
            </div>

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
